import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { Project, DocumentData } from "types";
import { DocType } from "./types";
import { useEnhancedDocuments } from "./hooks/useEnhancedDocuments";
import { DocumentAudienceSection } from "./DocumentAudienceSection";
import { SupportingDocuments } from "./SupportingDocuments";
import { DocumentVisibility } from "./supportingMetadata";
import { StatusIndictor } from "./StatusIndicator";
import { useClient } from "utils/client";
import { requestDownload } from "pages/Project/hooks/useCreateProject";
import { DocumentPreviewModal } from "./DocumentLibrary/DocumentPreviewModal";
import { ControlContext } from "context/Controls";
import { zipDownload } from "utils/documentZip";

export const OrganizedLegacyDocuments = ({
  project,
  onSelect,
}: {
  project: Project;
  onSelect: (types: DocType[]) => void;
}) => {
  const { t } = useTranslation();
  const client = useClient();
  const { displayNotification } = useContext(ControlContext);
  const definitions = useEnhancedDocuments(project);
  const [selected, setSelected] = useState<DocType[]>([]);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const select = (type: DocType, checked: boolean) => {
    const next = checked
      ? [...selected, type]
      : selected.filter((t) => t !== type);
    setSelected(next);
    onSelect(next);
  };
  const action = async (doc: DocumentData, download: boolean) => {
    setBusy(true);
    try {
      if (download)
        await zipDownload(
          [doc],
          project.projectdata.crn,
          project.projectdata.product_name,
          client,
        );
      else
        setPreview(
          doc.key
            ? await requestDownload(client, doc.key, project.projectdata.crn)
            : doc.location,
        );
    } catch {
      displayNotification?.(t("supportingDocuments.actionFailed"), "", "error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <label className="mt-5 inline-flex items-center gap-2 text-sm text-gray-600">
        <input
          type="checkbox"
          className="rounded border-gray-300 text-cyan-600"
          checked={
            definitions.length > 0 && selected.length === definitions.length
          }
          onChange={(e) => {
            const next = e.target.checked
              ? definitions.map((d) => d.type as DocType)
              : [];
            setSelected(next);
            onSelect(next);
          }}
        />
        {t("documentLibrary.selectGenerated")}
      </label>
      {(["external", "internal"] as DocumentVisibility[]).map((visibility) => (
        <DocumentAudienceSection key={visibility} visibility={visibility}>
          {definitions
            .filter((d) => d.visibility === visibility)
            .map((def) => {
              const files = project.documents.filter(
                (d) => d.type === def.type,
              );
              return (
                <div
                  key={def.type}
                  className="rounded-lg border border-gray-200 bg-white"
                >
                  <div className="flex flex-wrap items-center gap-3 p-4">
                    <input
                      type="checkbox"
                      aria-label={t(`documents.${def.label}.label`)}
                      checked={selected.includes(def.type as DocType)}
                      onChange={(e) =>
                        select(def.type as DocType, e.target.checked)
                      }
                      className="rounded border-gray-300 text-cyan-600"
                    />
                    <span className="min-w-0 flex-1 text-sm font-medium text-gray-900">
                      {t(`documents.${def.label}.label`)}
                    </span>
                    <span className="rounded bg-cyan-50 px-2 py-1 text-xs text-cyan-700">
                      CERTAIN
                    </span>
                    <StatusIndictor status={def.status} />
                  </div>
                  {!!files.length && (
                    <details className="border-t border-gray-100">
                      <summary className="cursor-pointer px-4 py-2 text-xs text-cyan-700">
                        {t("documentLibrary.languagesAndFiles", {
                          count: files.length,
                        })}
                      </summary>
                      <ul className="divide-y divide-gray-100 px-4">
                        {files.map((file, i) => (
                          <li
                            key={file.key || `${file.name}-${i}`}
                            className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm"
                          >
                            <span>
                              {file.lang
                                ? t(`languages.${file.lang}`)
                                : t("documentLibrary.unspecified")}{" "}
                              <span className="text-xs text-gray-500">
                                · {t("documents.table.revision")}{" "}
                                {file.version || "–"}
                              </span>
                            </span>
                            <div className="flex gap-3">
                              <button
                                disabled={busy}
                                onClick={() => action(file, false)}
                                className="text-xs text-cyan-700"
                              >
                                {t("documents.preview")}
                              </button>
                              <button
                                disabled={busy}
                                onClick={() => action(file, true)}
                                className="text-xs text-cyan-700"
                              >
                                {t("supportingDocuments.download")}
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              );
            })}
          <SupportingDocuments
            project={project}
            embedded
            visibility={visibility}
          />
        </DocumentAudienceSection>
      ))}
      <DocumentPreviewModal
        modalVisible={!!preview}
        setModalVisible={(visible) => {
          if (!visible) setPreview("");
        }}
        previewLocation={preview}
      />
    </div>
  );
};
